import { memo, useMemo } from 'react';
import { Flex } from 'antd';
import { ChartTooltipContentEcho } from '../ChartTooltipEcho';
import {
  Cell,
  BarChart as RechartsBarChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  Bar,
  XAxis,
} from 'recharts';
import type { BarChartData, ChartProps } from '@/types';

type BarChartEchoProps = Omit<ChartProps, 'className'>;
import { getBarChartColor } from './getBarChartColor';
import useChartInteraction from '@/hooks/useChartInteraction';

const BarChartEcho = ({
  style,
  data,
  tooltip = true,
  selectedSegment,
  setSelectedSegment,
  ariaLabel,
  onBarHover,
}: BarChartEchoProps) => {
  const { containerRef, handleClick } = useChartInteraction({
    data,
    selectedSegment,
    setSelectedSegment,
  });

  const transformedChartData: BarChartData[] = useMemo(() => {
    const entries = Object.entries(data);
    const barCount = entries.length;

    return entries.map(([key, value], index) => ({
      ...value,
      key,
      name: value.name,
      color: getBarChartColor(index, barCount),
      total: {
        amount: value.total.amount,
        currency: value.total.currency,
      },
      percentage: value.percentage,
    }));
  }, [data]);

  return (
    <Flex
      ref={containerRef}
      style={{ ...style, minHeight: 0 }}
      role="img"
      aria-label={ariaLabel}
    >
      <ResponsiveContainer width="100%" height="100%">
        <RechartsBarChart data={transformedChartData}>
          {tooltip && (
            <Tooltip
              cursor={false}
              isAnimationActive={true}
              allowEscapeViewBox={{ x: true }}
              animationDuration={1000}
              content={({ active, payload }: TooltipProps<number, string>) =>
                active && payload && payload[0] ? (
                  <ChartTooltipContentEcho
                    type="campaign-types"
                    payload={{
                      type: 'campaign-types',
                      name: payload[0].payload.name,
                      color: payload[0].payload.color ?? '',
                      total: payload[0].payload.total,
                      impressions: payload[0].payload.impressions,
                      percentage: payload[0].payload.percentage,
                    }}
                  />
                ) : null
              }
            />
          )}

          <Bar
            dataKey="total.amount"
            onClick={handleClick}
            minPointSize={2.5}
            data-testid="bar-chart-bar"
            onMouseEnter={onBarHover}
          >
            {transformedChartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.color}
                opacity={selectedSegment !== null && selectedSegment !== entry.name ? 0.25 : 1}
                style={{ cursor: 'pointer' }}
              />
            ))}
          </Bar>
          <XAxis tickLine={false} tick={false} height={1} />
        </RechartsBarChart>
      </ResponsiveContainer>
    </Flex>
  );
};

export default memo(BarChartEcho);
